import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Beams } from '@/components/ui/ethereal-beams-hero';
import {
  Film,
  ArrowRight,
  Clapperboard,
  LayoutDashboard,
  Rocket,
  Upload,
  DollarSign,
  Heart,
  Users,
  ChevronDown,
  BarChart3,
  Megaphone,
  Shield,
  Globe,
} from 'lucide-react';

/* ─────────────────────────────────────────────
   SCROLL FADE-IN (matches Landing.jsx)
   ───────────────────────────────────────────── */
const FadeIn = ({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.7s cubic-bezier(.16,1,.3,1) ${delay}ms, transform 0.7s cubic-bezier(.16,1,.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

/* ─────────────────────────────────────────────
   FAQ ACCORDION
   ───────────────────────────────────────────── */
const FAQItem = ({
  item,
  open,
  toggle,
}: {
  item: { q: string; a: string };
  open: boolean;
  toggle: () => void;
}) => (
  <button onClick={toggle} className="w-full text-left group" aria-expanded={open}>
    <div className="flex items-center justify-between py-5 border-b border-border/40">
      <span className="text-base md:text-lg font-medium pr-4 group-hover:text-indigo-400 transition-colors">
        {item.q}
      </span>
      <ChevronDown
        className={`w-5 h-5 shrink-0 text-muted-foreground transition-transform duration-300 ${
          open ? 'rotate-180 text-indigo-400' : ''
        }`}
      />
    </div>
    <div
      className="overflow-hidden transition-all duration-300"
      style={{ maxHeight: open ? '250px' : '0px', opacity: open ? 1 : 0 }}
    >
      <p className="text-muted-foreground text-sm md:text-base pb-5 pt-3 leading-relaxed">{item.a}</p>
    </div>
  </button>
);

/* ─────────────────────────────────────────────
   DATA
   ───────────────────────────────────────────── */

const HOW_IT_WORKS = [
  {
    step: '01',
    icon: <Upload className="w-5 h-5" />,
    title: 'Set Up Your Studio',
    description: 'Create your free publisher account. Add your studio name, logo, and brand page, it takes under 5 minutes.',
  },
  {
    step: '02',
    icon: <Film className="w-5 h-5" />,
    title: 'Upload Your Content',
    description: 'Upload films, series, or documentaries. Pay a one-time upload fee per title, then your content streams to every subscriber.',
  },
  {
    step: '03',
    icon: <Megaphone className="w-5 h-5" />,
    title: 'Launch a Campaign',
    description: 'Fund your next project before you film it. Set a slice goal and let subscribers back your vision directly.',
  },
  {
    step: '04',
    icon: <DollarSign className="w-5 h-5" />,
    title: 'Get Paid',
    description: 'Every slice donated to you is worth $1. Payouts go straight to your bank via Stripe Connect, Slice never takes a cut of donations.',
  },
];


const FEE_PILLS = [
  { icon: <Film className="w-3.5 h-3.5" />, label: 'Movie upload', value: '$100' },
  { icon: <Clapperboard className="w-3.5 h-3.5" />, label: 'Episode ≤35 min', value: '$25' },
  { icon: <Clapperboard className="w-3.5 h-3.5" />, label: 'Episode 36–60 min', value: '$50' },
  { icon: <Heart className="w-3.5 h-3.5" />, label: 'Bonus content', value: 'Free' },
];

const FEATURES = [
  { icon: <Film className="w-4 h-4" />, title: 'Upload Pipeline', description: 'Upload video → automated scan → human review → Mux encoding → live on the platform. Full pipeline visibility in your dashboard.' },
  { icon: <Megaphone className="w-4 h-4" />, title: 'Crowdfunding Campaigns', description: 'Fund before you film. Subscribers back your project with slices. Hit your goal, then produce with your audience already built in.' },
  { icon: <Globe className="w-4 h-4" />, title: 'Your Own Studio Page', description: 'Custom branded page with your logo, banner, about section, and full content library. Followers get notified when you publish.' },
  { icon: <BarChart3 className="w-4 h-4" />, title: 'Analytics Dashboard', description: 'Track views, slice donations, campaign progress, and follower growth all from one dashboard.' },
  { icon: <Users className="w-4 h-4" />, title: 'Direct Fan Connection', description: 'Subscribers choose to support you with their slices. No opaque algorithms deciding who gets paid, your fans fund you directly.' },
  { icon: <Shield className="w-4 h-4" />, title: 'Content Protection', description: 'Every upload goes through moderation before streaming. Deleted content is soft-vaulted, preserving access for supporters who donated slices.' },
];


const FAQ_ITEMS = [
  {
    q: 'Is it free to become a publisher?',
    a: 'Yes. Creating a publisher account is completely free. There are no monthly fees or platform subscriptions for publishers. You only pay per-title upload fees when you publish content.',
  },
  {
    q: 'What are the upload fees?',
    a: 'Movies, short films, and documentaries are $100 per title. TV episodes are $25–$50 depending on duration. Behind-the-scenes and bonus content is free when attached to a paid parent title. Fees are non-refundable and cover content moderation, encoding, and platform distribution.',
  },
  {
    q: 'How do I get paid?',
    a: 'You connect your bank account via Stripe Connect during setup. Every slice donated to your content is worth $1. Payouts happen automatically each month once you hit the $25 minimum threshold. Slice takes 0% of creator donations.',
  },
  {
    q: 'What happens if my content is rejected?',
    a: 'Every upload goes through human moderation. If rejected, you receive a reason and can appeal once. If the appeal is denied, you\'d need to re-upload (and pay the upload fee again). This ensures quality and legal compliance for all content on the platform.',
  },
  {
    q: 'Can I run a campaign before uploading content?',
    a: 'Yes and it\'s by design. "Fund before you film" is a core part of Slice. You can launch a crowdfunding campaign with just a pitch and optional trailer. Subscribers back your project with slices, and you produce it once funded.',
  },
  {
    q: 'Do I need to be a viewer to publish?',
    a: 'You need a Slice account, but you don\'t need a viewer subscription. One account handles both your personal viewer experience and your publisher brand identity. You can sign up and start publishing without subscribing as a viewer.',
  },
];

/* ─────────────────────────────────────────────
   MAIN COMPONENT
   ───────────────────────────────────────────── */

export default function Home() {
  const { isAuthenticated, canAccessPublisherPortal, publisher } = useAuth();
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const isLoggedIn = isAuthenticated();
  const isOnboarded = canAccessPublisherPortal();
  const hasPublisherRecord = !!publisher;

  /** Resolve the correct CTA link based on auth state */
  const getCtaLink = () => {
    if (isLoggedIn && isOnboarded) return '/dashboard';
    if (isLoggedIn && hasPublisherRecord) return '/onboarding';
    if (isLoggedIn) return '/select-partner-type';
    return '/auth?mode=signup';
  };

  const getCtaLabel = () => {
    if (isLoggedIn && isOnboarded) return 'Go to Dashboard';
    if (isLoggedIn && !isOnboarded) return hasPublisherRecord ? 'Continue Setup' : 'Start Your Journey';
    return 'Get Started';
  };

  const getCtaIcon = () => {
    if (isLoggedIn && isOnboarded) return <LayoutDashboard className="h-4 w-4" />;
    if (isLoggedIn && !isOnboarded) return <Rocket className="h-4 w-4" />;
    return <ArrowRight className="h-4 w-4" />;
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* ── Inline styles ── */}
      <style>{`
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .text-gradient-indigo {
          background: linear-gradient(135deg, #c7d2fe, #818cf8);
          background-size: 200% 200%;
          animation: gradient-shift 6s ease infinite;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      {/* ════════════════════════════════════════════
          HERO — Ethereal Beams
         ════════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-black" style={{ minHeight: '100vh' }}>
        {/* Beams background */}
        <div className="absolute inset-0 z-0">
          <Beams
            beamWidth={2.5}
            beamHeight={18}
            beamNumber={15}
            lightColor="#ffffff"
            speed={2.5}
            noiseIntensity={2}
            scale={0.15}
            rotation={43}
          />
        </div>

        {/* Radial indigo glow overlay */}
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 30% 50%, rgba(99,102,241,0.12) 0%, transparent 60%), ' +
              'radial-gradient(ellipse at 70% 20%, rgba(79,70,229,0.10) 0%, transparent 50%), ' +
              'radial-gradient(ellipse at 50% 0%, rgba(99,102,241,0.18) 0%, transparent 55%), ' +
              'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 40%, rgba(0,0,0,0.3) 100%)',
          }}
        />

        {/* Hero content */}
        <div className="relative z-10 flex min-h-[calc(100vh-3.5rem)] items-center py-32">
          <div className="max-w-4xl mx-auto px-6 w-full">
            <div className="flex flex-col items-center gap-7 text-center">

              

              {/* Headline */}
              <h1
                className="max-w-3xl text-4xl font-extrabold text-white md:text-6xl lg:text-7xl leading-[1.05] text-balance"
                style={{ letterSpacing: '-0.04em' }}
              >
                Your Content.{' '}
                <span className="text-gradient-indigo whitespace-nowrap">Your Audience.</span>{' '}
                <span className="whitespace-nowrap">Your Revenue.</span>
              </h1>

              {/* Subtext */}
              <p className="max-w-xl text-lg text-white/50 md:text-xl leading-relaxed">
                Upload films and series. Crowdfund your next project. Keep{' '}
                <strong className="text-white font-semibold">100% of every slice</strong> your fans donate, and
                Kinora never takes a cut of your slices.
              </p>

              {/* Auth-aware CTAs */}
              <div className="flex flex-col gap-4 sm:flex-row mt-2">
                <Button
                  size="lg"
                  className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-2xl shadow-indigo-500/25 px-8 h-12 text-base"
                  asChild
                >
                  <Link to={getCtaLink()} className="flex items-center gap-2">
                    {getCtaIcon()}
                    {getCtaLabel()}
                  </Link>
                </Button>

                {!isLoggedIn && (
                  <Button
                    size="lg"
                    className="rounded-full border border-white/20 bg-white/5 text-white backdrop-blur-xl hover:bg-white/10 hover:border-white/30 px-8 h-12 text-base"
                    asChild
                  >
                    <Link to="/auth">Sign In</Link>
                  </Button>
                )}
              </div>

              {/* Stats */}
              <div className="mt-6 flex border-t border-white/10 pt-8 w-full" style={{ maxWidth: '36rem' }}>
                {[
                  { value: 'Free', label: 'To Start Publishing' },
                  { value: '$1', label: 'Per Slice to You' },
                  { value: '0%', label: 'Platform Cut on Donations' },
                ].map((stat, i) => (
                  <div
                    key={stat.label}
                    className="flex-1 flex flex-col items-center gap-1 text-center"
                    style={{ borderRight: i < 2 ? '1px solid rgba(255,255,255,0.07)' : undefined }}
                  >
                    <span className="text-2xl font-extrabold text-white" style={{ letterSpacing: '-0.03em' }}>
                      {stat.value}
                    </span>
                    <span className="text-xs text-white/35">{stat.label}</span>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          HOW IT WORKS — Connected Flow
         ════════════════════════════════════════════ */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        <FadeIn className="text-center mb-14">
          <div className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-400 tracking-widest uppercase mb-3">
            <Rocket className="w-3 h-3" /> How It Works
          </div>
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight" style={{ letterSpacing: '-0.03em' }}>
            From Sign-Up to Payout in Four Steps
          </h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
            No gatekeepers. No approval process to join. Set up your studio,
            upload your work, and start earning.
          </p>
        </FadeIn>

        <FadeIn>
          <div className="relative flex items-start">
            {/* Connector line */}
            <div
              className="absolute top-[21px] z-0 hidden sm:block"
              style={{
                left: 'calc(12.5% + 21px)',
                right: 'calc(12.5% + 21px)',
                height: '1px',
                background: 'linear-gradient(90deg, rgba(79,70,229,0.6), rgba(99,102,241,0.3), rgba(129,140,248,0.5))',
              }}
            />

            {HOW_IT_WORKS.map((step) => (
              <div key={step.step} className="flex-1 flex flex-col items-center text-center px-3 relative z-10">
                <div
                  className="w-[42px] h-[42px] rounded-full flex items-center justify-center text-sm font-extrabold text-indigo-300 mb-4 shrink-0"
                  style={{
                    background: '#0d0d1f',
                    border: '1.5px solid #4f46e5',
                    boxShadow: '0 0 0 5px rgba(79,70,229,0.08)',
                  }}
                >
                  {step.step}
                </div>
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-indigo-400 mb-3 shrink-0"
                  style={{
                    background: 'rgba(99,102,241,0.08)',
                    border: '1px solid rgba(99,102,241,0.18)',
                  }}
                >
                  {step.icon}
                </div>
                <h3 className="text-sm font-bold mb-1.5 text-foreground">{step.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </FadeIn>
      </section>

      {/* ════════════════════════════════════════════
          THE MONEY MOMENT — Economics
         ════════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-black border-y border-white/[0.04]">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% 50%, rgba(79,70,229,0.12) 0%, transparent 65%)',
          }}
        />
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-24 relative text-center">
          <FadeIn>
            <div className="text-xs font-bold text-indigo-400 tracking-widest uppercase mb-4">
              The Economics, Plain and Simple
            </div>
            <h2 className="text-2xl md:text-4xl font-bold tracking-tight mb-12" style={{ letterSpacing: '-0.03em' }}>
              You Know Exactly What You Pay, and What You Keep
            </h2>

            <div className="flex items-center justify-center">
              {[
                { value: '$0', label: 'To join as a publisher.\nNo subscriptions, no platform fees.' },
                { value: '$1', label: 'Every slice donated to you.\n100% yours, always.' },
                { value: '0%', label: 'Platform cut on donations.\nWe never take from creator support.' },
              ].map((item, i) => (
                <div key={item.value} className="flex items-center">
                  <div className="flex flex-col items-center px-6 md:px-12">
                    <div
                      className="text-5xl md:text-7xl font-black text-indigo-200 leading-none"
                      style={{ letterSpacing: '-0.05em' }}
                    >
                      {item.value}
                    </div>
                    <div className="text-xs text-white/35 mt-3 leading-relaxed whitespace-pre-line max-w-[140px]">
                      {item.label}
                    </div>
                  </div>
                  {i < 2 && (
                    <div className="w-px h-20 bg-white/[0.07] shrink-0" />
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 justify-center mt-10">
              {FEE_PILLS.map((pill) => (
                <div
                  key={pill.label}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs text-white/45"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <span className="text-indigo-400/70">{pill.icon}</span>
                  {pill.label}{' '}
                  <strong className="text-white/70 font-semibold">{pill.value}</strong>
                </div>
              ))}
            </div>

            <p className="text-xs text-white/20 mt-6 max-w-md mx-auto leading-relaxed">
              Upload fees are one-time and non-refundable. They cover content moderation, encoding, and platform distribution.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          PLATFORM FEATURES — 2-col grid
         ════════════════════════════════════════════ */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        <FadeIn className="text-center mb-12">
          <div className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-400 tracking-widest uppercase mb-3">
            What You Get
          </div>
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight" style={{ letterSpacing: '-0.03em' }}>
            Everything You Need to Publish &amp; Grow
          </h2>
        </FadeIn>

        <FadeIn delay={80}>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 overflow-hidden rounded-xl"
            style={{ border: '1px solid #1a1a2e', background: '#111120' }}
          >
            {FEATURES.map((feature, i) => (
              <div
                key={feature.title}
                className="flex gap-4 items-start p-6"
                style={{
                  background: '#0d0d17',
                  borderRight: i % 2 === 0 ? '1px solid #111120' : undefined,
                  borderBottom: i < FEATURES.length - 2 ? '1px solid #111120' : undefined,
                }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-indigo-400"
                  style={{
                    background: 'rgba(99,102,241,0.08)',
                    border: '1px solid rgba(99,102,241,0.15)',
                  }}
                >
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-sm font-bold mb-1 text-foreground">{feature.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </FadeIn>
      </section>


      {/* ════════════════════════════════════════════
          FAQ
         ════════════════════════════════════════════ */}
      <section className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <FadeIn className="text-center mb-10">
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight">
            Frequently Asked Questions
          </h2>
        </FadeIn>

        <FadeIn delay={100}>
          <div>
            {FAQ_ITEMS.map((item, i) => (
              <FAQItem
                key={item.q}
                item={item}
                open={openFAQ === i}
                toggle={() => setOpenFAQ(openFAQ === i ? null : i)}
              />
            ))}
          </div>
        </FadeIn>
      </section>

      {/* ════════════════════════════════════════════
          FINAL CTA
         ════════════════════════════════════════════ */}
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-20 md:pt-14 md:pb-28">
        <FadeIn>
          <div className="relative overflow-hidden rounded-2xl bg-neutral-950 px-8 py-16 md:px-16 md:py-20">
            {/* Gradient backdrop */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-700/30 via-indigo-900/15 to-transparent" />
            {/* Glow orbs */}
            <div className="absolute -top-20 right-1/4 h-64 w-64 rounded-full bg-indigo-500/15 blur-[80px] pointer-events-none" />
            <div className="absolute -bottom-16 left-1/4 h-48 w-48 rounded-full bg-indigo-400/10 blur-[70px] pointer-events-none" />
            {/* Decorative rings */}
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full border border-white/[0.06]" />
            <div className="absolute -left-20 -bottom-20 h-64 w-64 rounded-full border border-white/[0.05]" />

            <div className="relative flex flex-col items-center gap-6 text-center max-w-lg mx-auto">
              

              <h2 className="text-3xl font-bold text-white md:text-5xl leading-tight">
                Ready to Share{' '}
                <span className="bg-gradient-to-r from-indigo-300 to-indigo-500 bg-clip-text text-transparent">
                  Your Story
                </span>
                ?
              </h2>

              <p className="text-white/55 text-base leading-relaxed">
                Create your free publisher account and get your content in front of subscribers
                who want to support independent creators, one slice at a time.
              </p>

              <div className="flex flex-col gap-4 sm:flex-row mt-2">
                <Button
                  size="lg"
                  className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-500/30 px-8 h-12 text-base"
                  asChild
                >
                  <Link to={getCtaLink()} className="flex items-center gap-2">
                    {getCtaIcon()}
                    {getCtaLabel()}
                  </Link>
                </Button>

                {!isLoggedIn && (
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-full border-white/30 bg-white/8 text-white hover:bg-white/15 hover:border-white/50 backdrop-blur-sm px-8 h-12 text-base"
                    asChild
                  >
                    <Link to="/auth">Sign In</Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-border/30 px-6 py-8">
        <div className="max-w-6xl mx-auto flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 text-sm font-bold text-white">
              S
            </div>
            <span className="font-semibold">Kinora</span>
          </div>
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Kinora. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
